type TFn = (key: string, fallback: string) => string;

/**
 * What a signing-audit event SAYS, and how it reads at a glance.
 *
 * Shared by `AuditTrailView` and by the activity panel, which merges these into the review's
 * own history as the prototype's `workflow` kind — "Sent the contract out for review"
 * (`ReviewerDocumentPage.jsx:56-57`). One table, so the same event cannot be worded two ways
 * on two halves of the same screen.
 *
 * Replaces a mechanical `action.replace(/_/g, ' ')` + Title Case inside `AuditTrailView`,
 * which was not localised at all: every viewer, in every language, read `ENVELOPE_SENT` as
 * "Envelope Sent". It also leaked the wire name's shape, so a reader saw the enum rather than
 * the event.
 */
export function auditActionLabel(action: string, t: TFn): string {
  switch (action) {
    case 'ENVELOPE_CREATED':
      return t('K_AUDIT_ACTION_ENVELOPE_CREATED', 'Created the request');
    case 'ENVELOPE_SENT':
      return t('K_AUDIT_ACTION_ENVELOPE_SENT', 'Sent the contract for signature');
    case 'ENVELOPE_VIEWED':
      return t('K_AUDIT_ACTION_ENVELOPE_VIEWED', 'Opened the contract');
    case 'FIELD_FILLED':
      return t('K_AUDIT_ACTION_FIELD_FILLED', 'Filled in a field');
    case 'ENVELOPE_SIGNED':
      return t('K_AUDIT_ACTION_ENVELOPE_SIGNED', 'Signed the contract');
    case 'ENVELOPE_DECLINED':
      return t('K_AUDIT_ACTION_ENVELOPE_DECLINED', 'Declined to sign');
    case 'ENVELOPE_VOIDED':
      return t('K_AUDIT_ACTION_ENVELOPE_VOIDED', 'Voided the request');
    case 'ENVELOPE_EXPIRED':
      return t('K_AUDIT_ACTION_ENVELOPE_EXPIRED', 'The request expired');
    case 'ENVELOPE_COMPLETED':
      return t('K_AUDIT_ACTION_ENVELOPE_COMPLETED', 'Everyone has signed');
    case 'ENVELOPE_RESENT':
      return t('K_AUDIT_ACTION_ENVELOPE_RESENT', 'Resent the contract');
    case 'ENVELOPE_REVISED':
      return t('K_AUDIT_ACTION_ENVELOPE_REVISED', 'Revised the contract');
    case 'ENVELOPE_EDITED':
      return t('K_AUDIT_ACTION_ENVELOPE_EDITED', 'Edited the request');
    case 'SCAN_UPLOADED':
      return t('K_AUDIT_ACTION_SCAN_UPLOADED', 'Uploaded a signed copy');
    case 'CC_ACKNOWLEDGED':
      return t('K_AUDIT_ACTION_CC_ACKNOWLEDGED', 'Acknowledged the copy');
    case 'ENVELOPE_SENT_FOR_REVIEW':
      return t('K_AUDIT_ACTION_ENVELOPE_SENT_FOR_REVIEW', 'Sent the contract out for review');
    case 'ENVELOPE_RESENT_FOR_REVIEW':
      return t(
        'K_AUDIT_ACTION_ENVELOPE_RESENT_FOR_REVIEW',
        'Sent the contract out for review again',
      );
    case 'ENVELOPE_REVIEW_COMPLETED':
      return t('K_AUDIT_ACTION_ENVELOPE_REVIEW_COMPLETED', 'The review finished');
    case 'ENVELOPE_REVIEW_CANCELLED':
      return t('K_AUDIT_ACTION_ENVELOPE_REVIEW_CANCELLED', 'Cancelled the review');
    case 'EMPLOYEE_HANDOFF_PUSHED':
      return t('K_AUDIT_ACTION_EMPLOYEE_HANDOFF_PUSHED', 'Handed the employee record over');
    case 'EMPLOYEE_HANDOFF_FAILED':
      return t(
        'K_AUDIT_ACTION_EMPLOYEE_HANDOFF_FAILED',
        'Could not hand the employee record over',
      );
    case 'EMPLOYEE_PROFILE_WRITEBACK_PUSHED':
      return t(
        'K_AUDIT_ACTION_EMPLOYEE_PROFILE_WRITEBACK_PUSHED',
        'Wrote the details back to the employee record',
      );
    case 'EMPLOYEE_PROFILE_WRITEBACK_FAILED':
      return t(
        'K_AUDIT_ACTION_EMPLOYEE_PROFILE_WRITEBACK_FAILED',
        'Could not write the details back to the employee record',
      );
    default:
      // Deliberately generic rather than a prettified wire name: an action added backend-first
      // should read as something that happened, not as an enum with its underscores removed.
      return t('K_AUDIT_ACTION_UNKNOWN', 'Updated the request');
  }
}

export type AuditDotTone = 'positive' | 'danger' | 'brand' | 'neutral';

/**
 * The dot's tone for an audit event.
 *
 * ⚠ Matched on the WHOLE action, not on a bare verb. The previous version tested
 * `['CREATED', 'SENT', 'SIGNED', 'COMPLETED'].includes(action.toUpperCase())` — but the enum
 * emits `ENVELOPE_CREATED`, `ENVELOPE_SENT`, `ENVELOPE_SIGNED`, so nothing ever matched and
 * EVERY dot in the audit trail rendered neutral. The colours have never been seen.
 */
export function auditDotTone(action: string): AuditDotTone {
  switch (action) {
    case 'ENVELOPE_CREATED':
    case 'ENVELOPE_SENT':
    case 'ENVELOPE_RESENT':
    case 'ENVELOPE_SIGNED':
    case 'ENVELOPE_COMPLETED':
    case 'SCAN_UPLOADED':
    case 'ENVELOPE_REVIEW_COMPLETED':
    case 'EMPLOYEE_HANDOFF_PUSHED':
    case 'EMPLOYEE_PROFILE_WRITEBACK_PUSHED':
      return 'positive';
    case 'ENVELOPE_DECLINED':
    case 'ENVELOPE_VOIDED':
    case 'ENVELOPE_EXPIRED':
    case 'ENVELOPE_REVIEW_CANCELLED':
    case 'EMPLOYEE_HANDOFF_FAILED':
    case 'EMPLOYEE_PROFILE_WRITEBACK_FAILED':
      return 'danger';
    case 'ENVELOPE_VIEWED':
    case 'CC_ACKNOWLEDGED':
    case 'ENVELOPE_SENT_FOR_REVIEW':
    case 'ENVELOPE_RESENT_FOR_REVIEW':
      return 'brand';
    default:
      return 'neutral';
  }
}
